import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Row, Col, Card, Table, Button, Form, Badge, Modal, Spinner, Alert, InputGroup } from 'react-bootstrap';
import CreatableSelect from 'react-select/creatable';
import adminService from '../../services/adminService';
import usePagination from '../../hooks/usePagination';
import TablePagination from '../../components/TablePagination';

const SEED_GROUPS = ['Oil & Gas', 'Construction', 'Steel & Manufacturing', 'General', 'Others'];

const EMPTY_FORM = {
  client_code: '',
  client_name: '',
  client_group: '',
  client_location: '',
  contact_email: '',
  contact_phone: '',
  join_date: '',
};

const ManageClientsSection = () => {
  const [clients, setClients] = useState([]);
  const [kpis, setKpis] = useState({ total_clients: 0, client_groups: 0, locations: 0 });
  const [groupOptions, setGroupOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const [searchInput, setSearchInput] = useState('');
  const [groupFilter, setGroupFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const [showModal, setShowModal] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [statusTarget, setStatusTarget] = useState(null);
  const [statusError, setStatusError] = useState('');
  const [updatingStatus, setUpdatingStatus] = useState(false);

  const { page, setPage, totalPages, paginatedItems: paginatedClients } = usePagination(clients);

  const fetchClients = useCallback(async (search = '', group = '', status = '') => {
    setLoading(true);
    setLoadError('');
    try {
      const params = {};
      if (search) params.search = search;
      if (group) params.client_group = group;
      if (status) params.status = status;
      const res = await adminService.getClients(params);
      if (res.success) {
        setClients(res.clients);
        setKpis(res.kpis);
        const merged = Array.from(new Set([...SEED_GROUPS, ...(res.client_groups || [])]));
        setGroupOptions(merged);
      } else {
        setLoadError(res.message || 'Failed to load clients');
      }
    } catch (err) {
      console.error('Failed to load clients:', err);
      setLoadError(err.message || 'Failed to reach the server. Is the backend running?');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  const creatableOptions = useMemo(
    () => groupOptions.map(g => ({ label: g, value: g })),
    [groupOptions]
  );

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchClients(searchInput, groupFilter, statusFilter);
  };

  const handleClearFilters = () => {
    setSearchInput('');
    setGroupFilter('');
    setStatusFilter('');
    setPage(1);
    fetchClients('', '', '');
  };

  const openAddModal = () => {
    setEditingClient(null);
    setFormData(EMPTY_FORM);
    setFormError('');
    setShowModal(true);
  };

  const openEditModal = (client) => {
    setEditingClient(client);
    setFormData({
      client_code: client.customer_code || '',
      client_name: client.client_name || '',
      client_group: client.client_group || '',
      client_location: client.client_location || '',
      contact_email: client.contact_email || '',
      contact_phone: client.contact_phone || '',
      join_date: client.join_date || '',
    });
    setFormError('');
    setShowModal(true);
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    setSubmitting(true);
    try {
      if (editingClient) {
        const res = await adminService.updateClient(editingClient.client_id, {
          client_name: formData.client_name,
          client_group: formData.client_group,
          client_location: formData.client_location,
          contact_email: formData.contact_email,
          contact_phone: formData.contact_phone,
          join_date: formData.join_date,
        });
        if (!res.success) throw new Error(res.message);
      } else {
        const res = await adminService.createClient(formData);
        if (!res.success) throw new Error(res.message);
      }
      setShowModal(false);
      fetchClients(searchInput, groupFilter, statusFilter);
    } catch (err) {
      setFormError(err.message || 'Something went wrong');
    } finally {
      setSubmitting(false);
    }
  };

  const confirmStatusToggle = (client) => {
    setStatusTarget(client);
    setStatusError('');
  };

  const handleStatusToggle = async () => {
    if (!statusTarget) return;
    const nextActive = !statusTarget.is_active;
    setUpdatingStatus(true);
    setStatusError('');
    try {
      const res = await adminService.setClientStatus(statusTarget.client_id, nextActive);
      if (!res.success) throw new Error(res.message);
      setStatusTarget(null);
      fetchClients(searchInput, groupFilter, statusFilter);
    } catch (err) {
      setStatusError(err.message || 'Failed to update client status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  return (
    <div>
      {loadError && (
        <Alert variant="danger">
          <i className="fas fa-exclamation-triangle me-2"></i>
          {loadError}
        </Alert>
      )}
      {/* KPIs */}
      <Row className="mb-4">
        <Col md={4} className="mb-3">
          <Card className="text-center shadow-sm h-100 border-primary">
            <Card.Body>
              <i className="fas fa-building text-primary mb-2" style={{ fontSize: '2rem' }}></i>
              <h3 className="fw-bold mb-0">{kpis.total_clients}</h3>
              <p className="text-muted mb-0">Total Clients</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4} className="mb-3">
          <Card className="text-center shadow-sm h-100 border-info">
            <Card.Body>
              <i className="fas fa-layer-group text-info mb-2" style={{ fontSize: '2rem' }}></i>
              <h3 className="fw-bold mb-0">{kpis.client_groups}</h3>
              <p className="text-muted mb-0">Client Groups</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4} className="mb-3">
          <Card className="text-center shadow-sm h-100 border-warning">
            <Card.Body>
              <i className="fas fa-map-marker-alt text-warning mb-2" style={{ fontSize: '2rem' }}></i>
              <h3 className="fw-bold mb-0">{kpis.locations}</h3>
              <p className="text-muted mb-0">Locations</p>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Filters + Add button */}
      <Card className="shadow-sm mb-3">
        <Card.Body>
          <Form onSubmit={handleSearch}>
            <Row className="g-2 align-items-end">
              <Col md={3}>
                <Form.Label className="small text-muted mb-1">Search</Form.Label>
                <InputGroup>
                  <InputGroup.Text><i className="fas fa-search"></i></InputGroup.Text>
                  <Form.Control
                    placeholder="Code, name, location or email"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                  />
                </InputGroup>
              </Col>
              <Col md={3}>
                <Form.Label className="small text-muted mb-1">Client Group</Form.Label>
                <Form.Select value={groupFilter} onChange={(e) => setGroupFilter(e.target.value)}>
                  <option value="">All Groups</option>
                  {groupOptions.map(g => <option key={g} value={g}>{g}</option>)}
                </Form.Select>
              </Col>
              <Col md={2}>
                <Form.Label className="small text-muted mb-1">Status</Form.Label>
                <Form.Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                  <option value="">All</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </Form.Select>
              </Col>
              <Col md="auto">
                <Button type="submit" variant="primary"><i className="fas fa-search me-1"></i>Search</Button>
              </Col>
              <Col md="auto">
                <Button type="button" variant="outline-secondary" onClick={handleClearFilters}>Clear</Button>
              </Col>
              <Col className="text-end">
                <Button variant="success" onClick={openAddModal}>
                  <i className="fas fa-plus me-1"></i>Add Client
                </Button>
              </Col>
            </Row>
          </Form>
        </Card.Body>
      </Card>

      {/* Table */}
      <Card className="shadow-sm">
        <Card.Body>
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : (
            <div className="table-responsive">
              <Table hover className="align-middle mb-0">
                <thead className="table-dark">
                  <tr>
                    <th>Client Code</th>
                    <th>Name</th>
                    <th>Group</th>
                    <th>Location</th>
                    <th>Contact Email</th>
                    <th>Contact Phone</th>
                    <th>Join Date</th>
                    <th>Status</th>
                    <th>Registration Status</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {clients.length === 0 ? (
                    <tr>
                      <td colSpan={10} className="text-center text-muted py-4">No clients found</td>
                    </tr>
                  ) : (
                    paginatedClients.map(client => (
                      <tr key={client.client_id}>
                        <td><strong>{client.customer_code}</strong></td>
                        <td>{client.client_name}</td>
                        <td>{client.client_group}</td>
                        <td>{client.client_location}</td>
                        <td>{client.contact_email || <span className="text-muted fst-italic">—</span>}</td>
                        <td>{client.contact_phone || <span className="text-muted fst-italic">—</span>}</td>
                        <td>{client.join_date}</td>
                        <td>
                          {client.is_active
                            ? <Badge bg="success">Active</Badge>
                            : <Badge bg="secondary">Inactive</Badge>}
                        </td>
                        <td>
                          {client.login_status === 'blocked' ? (
                            <Badge bg="danger">Blocked</Badge>
                          ) : client.login_status === 'registered' ? (
                            <Badge bg="success">Registered</Badge>
                          ) : (
                            <Badge bg="secondary">Not Registered</Badge>
                          )}
                        </td>
                        <td className="text-end">
                          <Button size="sm" variant="outline-primary" className="me-2" title="Edit" onClick={() => openEditModal(client)}>
                            <i className="fas fa-edit"></i>
                          </Button>
                          {client.is_active ? (
                            <Button size="sm" variant="outline-danger" title="Deactivate" onClick={() => confirmStatusToggle(client)}>
                              <i className="fas fa-user-slash"></i>
                            </Button>
                          ) : (
                            <Button size="sm" variant="outline-success" title="Activate" onClick={() => confirmStatusToggle(client)}>
                              <i className="fas fa-user-check"></i>
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
              <TablePagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Add / Edit Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Form onSubmit={handleSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>{editingClient ? 'Edit Client' : 'Add Client'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {formError && <Alert variant="danger">{formError}</Alert>}
            <Form.Group className="mb-3">
              <Form.Label>Client Code</Form.Label>
              <Form.Control
                value={formData.client_code}
                onChange={(e) => handleFormChange('client_code', e.target.value)}
                disabled={!!editingClient}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Client Name</Form.Label>
              <Form.Control
                value={formData.client_name}
                onChange={(e) => handleFormChange('client_name', e.target.value)}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Client Group</Form.Label>
              <CreatableSelect
                options={creatableOptions}
                value={formData.client_group ? { label: formData.client_group, value: formData.client_group } : null}
                onChange={(option) => handleFormChange('client_group', option ? option.value : '')}
                placeholder="Select or type a new group..."
                isClearable
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Client Location</Form.Label>
              <Form.Control
                value={formData.client_location}
                onChange={(e) => handleFormChange('client_location', e.target.value)}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Contact Email</Form.Label>
              <Form.Control
                type="email"
                value={formData.contact_email}
                onChange={(e) => handleFormChange('contact_email', e.target.value)}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Contact Phone</Form.Label>
              <Form.Control
                value={formData.contact_phone}
                onChange={(e) => handleFormChange('contact_phone', e.target.value)}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Join Date</Form.Label>
              <Form.Control
                type="date"
                value={formData.join_date}
                onChange={(e) => handleFormChange('join_date', e.target.value)}
                required
              />
            </Form.Group>
            {!editingClient && (
              <p className="text-muted small mb-0">
                <i className="fas fa-info-circle me-1"></i>
                New clients are set to <strong>Active</strong> by default.
              </p>
            )}
            {editingClient && (
              <p className="text-muted small mb-0">
                <i className="fas fa-info-circle me-1"></i>
                Use the Activate/Deactivate button in the table to change this client's active status.
              </p>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button variant="primary" type="submit" disabled={submitting}>
              {submitting ? <Spinner animation="border" size="sm" /> : (editingClient ? 'Save Changes' : 'Add Client')}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Deactivate / Activate confirmation */}
      <Modal show={!!statusTarget} onHide={() => setStatusTarget(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{statusTarget?.is_active ? 'Deactivate Client' : 'Activate Client'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {statusError && <Alert variant="danger">{statusError}</Alert>}
          {statusTarget && (
            statusTarget.is_active ? (
              <>
                <p>
                  Are you sure you want to deactivate <strong>{statusTarget.client_name}</strong> ({statusTarget.customer_code})?
                </p>
                <Alert variant="warning" className="mb-0">
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  This hides the client from new inspection forms/reports, and
                  {statusTarget.registered
                    ? ' immediately blocks their existing client-portal login.'
                    : ' prevents them from registering on the client portal until reactivated.'}
                </Alert>
              </>
            ) : (
              <p>
                Are you sure you want to activate <strong>{statusTarget.client_name}</strong> ({statusTarget.customer_code})?
                They will reappear in inspection forms/reports and be able to log in (or register) again.
              </p>
            )
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setStatusTarget(null)}>Cancel</Button>
          <Button
            variant={statusTarget?.is_active ? 'danger' : 'success'}
            onClick={handleStatusToggle}
            disabled={updatingStatus}
          >
            {updatingStatus ? <Spinner animation="border" size="sm" /> : (statusTarget?.is_active ? 'Deactivate' : 'Activate')}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ManageClientsSection;
